import React, { useMemo, useState, useCallback, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { DragonShieldProducts, LuckDragonProducts, ArmyPainterProducts, SleeveKingsProducts, GradedGuardProducts, DiceProducts, formatZAR } from '../Components/ProductRetrieval'
import Pagination from '../Components/Pagination/Pagination'
import { useCart } from '../Components/Cart'
import { useFilterParams, FilterBar } from '../Components/FilterBar'

const ITEMS_PER_PAGE = 20

const BRAND_LABEL_TO_ID = {
  'Dragon Shield': 'dragonShield',
  'Luck Dragon': 'luckDragon',
  'Army Painter': 'armyPainter',
  'Sleeve Kings': 'sleeveKings',
  'Graded Guard': 'gradedGuard',
  'Dice': 'dice',
}

const BRANDS = [
  { id: 'dragonShield', label: 'Dragon Shield' },
  { id: 'luckDragon', label: 'Luck Dragon' },
  { id: 'armyPainter', label: 'Army Painter' },
  { id: 'sleeveKings', label: 'Sleeve Kings' },
  { id: 'gradedGuard', label: 'Graded Guard' },
  { id: 'dice', label: 'Dice' },
]

const brandArrays = {
  dragonShield: DragonShieldProducts,
  luckDragon: LuckDragonProducts,
  armyPainter: ArmyPainterProducts,
  sleeveKings: SleeveKingsProducts,
  gradedGuard: GradedGuardProducts,
  dice: DiceProducts,
}

const TcgAccessories = () => {
  const { setParam, setCurrentPage, read } = useFilterParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const brandFromUrl = searchParams.get('brand')
  const initialBrand = brandFromUrl
    ? (BRAND_LABEL_TO_ID[brandFromUrl] || BRANDS.find(b => b.id === brandFromUrl)?.id || 'dragonShield')
    : 'dragonShield'

  const [activeBrand, setActiveBrand] = useState(initialBrand)
  const [justAddedId, setJustAddedId] = useState(null)
  const { addToCart } = useCart()

  useEffect(() => {
    const brand = searchParams.get('brand')
    const resolved = brand
      ? (BRAND_LABEL_TO_ID[brand] || BRANDS.find(b => b.id === brand)?.id || 'dragonShield')
      : 'dragonShield'
    setActiveBrand(resolved)
    window.scrollTo(0, 0)
  }, [searchParams])

  const currentPage = parseInt(read('page', '1'))
  const sortBy = read('sort', 'name-asc')
  const searchTerm = read('q')
  const category = read('category', 'All')

  const handleAddToCart = useCallback((product) => {
    addToCart(product.id, product.name)
    setJustAddedId(product.id)
    setTimeout(() => setJustAddedId(null), 2000)
  }, [addToCart])

  const baseProducts = useMemo(() => brandArrays[activeBrand] || [], [activeBrand])

  const filteredProducts = useMemo(() => {
    let results = [...baseProducts]
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      results = results.filter(p => p.name.toLowerCase().includes(term))
    }
    if (category !== 'All') {
      results = results.filter(p => p.category === category)
    }
    switch (sortBy) {
      case 'name-asc': results.sort((a, b) => a.name.localeCompare(b.name)); break
      case 'name-desc': results.sort((a, b) => b.name.localeCompare(a.name)); break
      case 'price-asc': results.sort((a, b) => a.price - b.price); break
      case 'price-desc': results.sort((a, b) => b.price - a.price); break
      default: break
    }
    return results
  }, [baseProducts, searchTerm, category, sortBy])

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE)
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const handleBrandSwitch = useCallback((id) => {
    setActiveBrand(id)
    setSearchParams({ brand: id }, { replace: true })
  }, [setSearchParams])

  return (
    <div className="min-h-screen bg-[#eef2ff] text-gray-900">
      <section className="border-b border-gray-200 bg-gradient-to-br from-indigo-800 via-indigo-700 to-purple-800 px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-indigo-200">
            Tagers Shoppe
          </p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            TCG Accessories
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-indigo-100">
            Sleeves, cases and storage for your trading cards — all priced in
            South African Rand and ready for nationwide shipping.
          </p>
          <p className="mt-3 text-sm text-indigo-200">
            All prices include VAT.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-wrap gap-3 border-b border-gray-200 pb-3 text-sm font-semibold">
          {BRANDS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleBrandSwitch(tab.id)}
              className={`rounded-full border px-4 py-2 transition ${
                activeBrand === tab.id
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-indigo-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <FilterBar
          category="accessories"
          categoryValue={category}
          onCategoryChange={(v) => setParam('category', v)}
          sortBy={sortBy}
          onSortChange={(v) => setParam('sort', v)}
          searchTerm={searchTerm}
          onSearchChange={(v) => setParam('q', v)}
          searchPlaceholder="Search accessories…"
          onClear={() => {}}
          activeFilterCount={(category !== 'All' ? 1 : 0)}
          resultCount={paginatedProducts.length}
          totalCount={filteredProducts.length}
          resultUnit="TCG accessories"
          colorScheme="indigo"
        />

        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          colorScheme="indigo"
        /> <br/>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedProducts.map((product) => {
            const isOutOfStock = product.inStock === 0
            const isPreorder = product.inStock === 2

            return (
              <article
                key={product.id}
                className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              >
                <Link to={`/Product/${product.id}`} className="flex flex-1 flex-col">
                  <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-200">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className={`h-full w-full object-contain max-h-[280px] ${isOutOfStock ? 'grayscale' : ''}`}
                      />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                        <span className="text-sm font-medium text-gray-500">No image available</span>
                      </div>
                    )}
                    {isOutOfStock && (
                      <span className="absolute left-3 top-3 rounded-full bg-gray-600 px-2.5 py-1 text-xs font-semibold text-white">
                        Out of stock
                      </span>
                    )}
                    {isPreorder && (
                      <span className="absolute left-3 top-3 rounded-full bg-amber-500 px-2.5 py-1 text-xs font-semibold text-white">
                        Pre-order
                      </span>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col p-4 text-left">
                    <h2 className={`line-clamp-2 text-base font-semibold ${
                      isOutOfStock ? 'text-gray-500' : 'text-gray-900 group-hover:text-indigo-600'
                    }`}>
                      {product.name}
                    </h2>
                    <p className={`mt-auto pt-4 text-lg font-bold ${isOutOfStock ? 'text-gray-400' : 'text-gray-900'}`}>
                      {formatZAR(product.price)}
                    </p>
                  </div>
                </Link>

                <div className="px-4 pb-4">
                  <button
                    onClick={() => handleAddToCart(product)}
                    disabled={isOutOfStock}
                    className={`w-full rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${
                      isOutOfStock
                        ? 'bg-gray-400 cursor-not-allowed'
                        : justAddedId === product.id
                        ? 'bg-indigo-600'
                        : 'bg-indigo-700 hover:bg-indigo-800'
                    }`}
                  >
                    {justAddedId === product.id
                      ? 'Added to cart'
                      : isOutOfStock
                      ? 'Out of stock'
                      : 'Add to cart'}
                  </button>
                </div>
              </article>
            )
          })}
        </div>

        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          colorScheme="indigo"
        />
      </section>
    </div>
  )
}

export default TcgAccessories
